import ExpandIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Grid, Paper, TextField, Typography } from '@mui/material';
import { get } from 'lodash';
import { default as React } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { CasesParams } from 'utils/useCasesParams/types';
import { ChannelsController } from '../../ChannelsController';
import {
  DEFAULT_CELL_SUBSAMPLING_PARAMS,
  DEFAULT_LOUVAIN_PARAMS,
  MifMarkerUnsupervisedAnalysisFormValues,
} from './types';

export interface AdditionalParametersStepProps {
  casesParams: CasesParams;
  methods: UseFormReturn<MifMarkerUnsupervisedAnalysisFormValues, any>;
  checkValidationAndSetIsStepFailed: () => void;
}

export const AdditionalParametersStep: React.FC<AdditionalParametersStepProps> = ({
  casesParams,
  methods,
  checkValidationAndSetIsStepFailed,
}) => {
  const {
    control,
    formState: { errors },
  } = methods;

  const handleNumberFieldChange = (
    onChange: (value: number | null) => void,
    value: string,
    defaultValue: number | null,
    allowEmpty = false
  ) => {
    if (value === '') {
      onChange(allowEmpty ? null : defaultValue);
      void checkValidationAndSetIsStepFailed();
      return;
    }

    onChange(Number(value));
    void checkValidationAndSetIsStepFailed();
  };

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item>
        <ChannelsController
          name="configParams.markers"
          error={Boolean(get(errors, 'configParams.markers'))}
          helperText={get(errors, 'configParams.markers')?.message}
          methods={methods}
          selectAllOnLoad
          additionalOnChange={() => {
            checkValidationAndSetIsStepFailed();
          }}
          casesParams={casesParams}
        />
      </Grid>

      <Grid item>
        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandIcon />}>Advanced settings</AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ height: '100%', p: 2 }}>
                  <Grid container direction="column" spacing={2}>
                    <Grid item>
                      <Typography variant="body1">Cell subsampling</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Defaults are already applied. Adjust these only if you need to change how cells are sampled per
                        slide before clustering.
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Controller
                        name="configParams.cellSubsamplingParams.capPerSlide"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Per-slide cap"
                            type="number"
                            value={field.value ?? ''}
                            onChange={(event) =>
                              handleNumberFieldChange(
                                field.onChange,
                                event.target.value,
                                DEFAULT_CELL_SUBSAMPLING_PARAMS.capPerSlide
                              )
                            }
                            error={Boolean(get(errors, 'configParams.cellSubsamplingParams.capPerSlide'))}
                            helperText={
                              get(errors, 'configParams.cellSubsamplingParams.capPerSlide')?.message ||
                              `Default: ${DEFAULT_CELL_SUBSAMPLING_PARAMS.capPerSlide}`
                            }
                            inputProps={{ min: 1, step: 1 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item>
                      <Controller
                        name="configParams.cellSubsamplingParams.totalN"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Target cells per slide"
                            type="number"
                            value={field.value ?? ''}
                            onChange={(event) =>
                              handleNumberFieldChange(
                                field.onChange,
                                event.target.value,
                                DEFAULT_CELL_SUBSAMPLING_PARAMS.totalN
                              )
                            }
                            error={Boolean(get(errors, 'configParams.cellSubsamplingParams.totalN'))}
                            helperText={
                              get(errors, 'configParams.cellSubsamplingParams.totalN')?.message ||
                              `Default: ${DEFAULT_CELL_SUBSAMPLING_PARAMS.totalN}`
                            }
                            inputProps={{ min: 1, step: 1 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item>
                      <Controller
                        name="configParams.cellSubsamplingParams.threshold"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Positivity threshold"
                            type="number"
                            value={field.value ?? ''}
                            onChange={(event) =>
                              handleNumberFieldChange(
                                field.onChange,
                                event.target.value,
                                DEFAULT_CELL_SUBSAMPLING_PARAMS.threshold
                              )
                            }
                            error={Boolean(get(errors, 'configParams.cellSubsamplingParams.threshold'))}
                            helperText={
                              get(errors, 'configParams.cellSubsamplingParams.threshold')?.message ||
                              `Default: ${DEFAULT_CELL_SUBSAMPLING_PARAMS.threshold}`
                            }
                            inputProps={{ min: 0, max: 1, step: 0.01 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item>
                      <Controller
                        name="configParams.cellSubsamplingParams.minGroupSize"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Minimum group size"
                            type="number"
                            value={field.value ?? ''}
                            onChange={(event) =>
                              handleNumberFieldChange(
                                field.onChange,
                                event.target.value,
                                DEFAULT_CELL_SUBSAMPLING_PARAMS.minGroupSize
                              )
                            }
                            error={Boolean(get(errors, 'configParams.cellSubsamplingParams.minGroupSize'))}
                            helperText={
                              get(errors, 'configParams.cellSubsamplingParams.minGroupSize')?.message ||
                              `Default: ${DEFAULT_CELL_SUBSAMPLING_PARAMS.minGroupSize}`
                            }
                            inputProps={{ min: 1, step: 1 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item>
                      <Controller
                        name="configParams.cellSubsamplingParams.topK"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Top groups to sample"
                            type="number"
                            value={field.value ?? ''}
                            onChange={(event) =>
                              handleNumberFieldChange(
                                field.onChange,
                                event.target.value,
                                DEFAULT_CELL_SUBSAMPLING_PARAMS.topK
                              )
                            }
                            error={Boolean(get(errors, 'configParams.cellSubsamplingParams.topK'))}
                            helperText={
                              get(errors, 'configParams.cellSubsamplingParams.topK')?.message ||
                              `Default: ${DEFAULT_CELL_SUBSAMPLING_PARAMS.topK}`
                            }
                            inputProps={{ min: 1, step: 1 }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ height: '100%', p: 2 }}>
                  <Grid container direction="column" spacing={2}>
                    <Grid item>
                      <Typography variant="body1">Louvain clustering</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Leave the dimensions override blank to let the workflow auto-compute it from the selected
                        markers.
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Controller
                        name="configParams.louvainParams.nDims"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Louvain dimensions override"
                            type="number"
                            value={field.value ?? ''}
                            onChange={(event) =>
                              handleNumberFieldChange(field.onChange, event.target.value, null, true)
                            }
                            error={Boolean(get(errors, 'configParams.louvainParams.nDims'))}
                            helperText={
                              get(errors, 'configParams.louvainParams.nDims')?.message ||
                              'Leave blank to auto-compute from the selected markers.'
                            }
                            inputProps={{ min: 1, step: 1 }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item>
                      <Controller
                        name="configParams.louvainParams.resolution"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            size="small"
                            label="Louvain resolution"
                            type="number"
                            value={field.value ?? ''}
                            onChange={(event) =>
                              handleNumberFieldChange(
                                field.onChange,
                                event.target.value,
                                DEFAULT_LOUVAIN_PARAMS.resolution
                              )
                            }
                            error={Boolean(get(errors, 'configParams.louvainParams.resolution'))}
                            helperText={
                              get(errors, 'configParams.louvainParams.resolution')?.message ||
                              `Default: ${DEFAULT_LOUVAIN_PARAMS.resolution}`
                            }
                            inputProps={{ min: 0.01, step: 0.05 }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
};
